import "moment/locale/de";
import { LanguageTemplate } from "../template";
import { ActionsHandler, GetActionBuilder } from "../types";

const action_builder: ActionsHandler = {
  create: {
    title: (type: string) => `${type} erstellen`,
    success: (type: string) => `${type} erstellt`,
  },
  update: {
    title: (type: string) => `${type} bearbeiten`,
    success: (type: string) => `${type} bearbeitet`,
  },
  delete: {
    title: (type: string) => `${type} löschen`,
    success: (type: string) => `${type} gelöscht`,
  },
};

export const de_de: LanguageTemplate = {
  moment_lang_key: "de",
  langs: {
    de_de: "Deutsch",
    en_us: "English US",
  },
  actions: {
    logout: "Ausloggen",
    save: "Speichern",
    show: "Anzeigen",
    edit: "Bearbeiten",
    create: "Erstellen",
    delete: "Löschen",
    add: "Hinzufügen",
    label: "Aktionen",
    open: "Öffnen",
    close: "Schließen",
    cancel: "Abbrechen",
    copy: "Kopiert",
    toCopy: "Kopieren",
    markProcessed: "Fertigstellen",
    markPickedup: "Abgeholt",
    printLabel: "Label drucken",
    print: "Drucken",
    showArticles: "Artikel anzeigen",
    select: (t) => (t ? `${t} auswählen` : "auswählen"),
    days: (d) => `${d} Tage`,
    followLink: "Link folgen",
    builder: action_builder,
  },
  menu: {
    supply: {
      article_sources: "Artikel Bezüge",
      distributors: "Lieferanten",
      root_label: "Einkauf",
    },
  },
  results: {
    saved: "Gespeichert",
    deleted: "Gelöscht",
  },
  errors: {
    generic: "Es ist ein Unbekannter Fehler aufgetreten",
    loginError: "Ein Fehler beim login ist aufgetrefen",
    login: { invalid: "Ungültige Zugangsdaten" },
    schema: {
      invalid: "Ungültige Informationen",
      not_found: {
        data: "Datensatz nicht gefunden",
        param: "Fehlender Parameter",
      },
      unique: (keys, model) =>
        `Ein ${model} mit dieser kombination von '${keys.join(
          ", "
        )}' existiert bereits`,
    },
    no_items: "Keine Auswahlmöglichkeit",
    "auth.invalid.information": "Ungültige Zugangsdaten",
  },
  pages: {
    profile: {
      title: "Profil",
      update_success: "Profil aktualisiert",
    },
    login: {
      password: "Passwort",
      userId: "Benutzer",
      title: "Login",
      success: "Login successfull!",
    },
    dashboard: {
      all_articles: "Alle Artikel",
      article_view: "Artikel Liste",
    },
    footprint: {
      list: "Bauformen",
      actions: GetActionBuilder("Bauform", action_builder),
    },
    location: {
      list: "Lagerorte",
      actions: GetActionBuilder("Lagerort", action_builder),
    },
    productGroup: {
      list: "Warengruppen",
      actions: GetActionBuilder("Warengruppe", action_builder),
    },
    articleCategory: {
      list: "Artikelkategorien",
      label: "Artikelkategorie",
      actions: GetActionBuilder("Artikelkategorie", action_builder),
    },
    keyField: {
      list: "Schlüsselfelder",
      label: "Schlüsselfeld",
      actions: GetActionBuilder("Schlüsselfeld", action_builder),
    },
    article: {
      list: "Artikel",
      view: "Artikel Eigenschaften",
      actions: GetActionBuilder("Artikel", action_builder),
      parameter: {
        fix_required: "Fix",
        fix_required_tooltip: "Füge fehlende Parameter hinzu",
      },
    },
    distributor: {
      list: "Lieferanten",
      actions: GetActionBuilder("Lieferant", action_builder),
    },
    articleSource: {
      list: "Bezugsquellen",
      view: "Bezugsquelle",
      actions: GetActionBuilder("Bezugsquelle", action_builder),
    },
    articleListView: {
      label: "Artikel Ansicht",
      select_category: "Bitte Warengruppe auswählen",
      no_items_in_category: "Diese Warengruppe ist leer",
    },
    customerOrder: {
      actions: GetActionBuilder("Kundenbestellung", action_builder),
      list: "Kundenbestellungen",
      view: "Kundenbestellung",
      orderPositions: "Positionen",
    },
    customer: {
      list: {
        label: "Kundenliste",
      },
      menu_label: "Kunden",
      actions: GetActionBuilder("Kunde", action_builder),
    },
    userlist: {
      label: "Benutzer Verwaltung",
      actions: GetActionBuilder("Benutzer", action_builder),
    },
  },
  global: {
    langSelector: {
      language: "Sprache",
    },
    deleteModal: {
      warning: (id: number, type: string, display?: string) => (
        <>
          <strong>
            Achtung! Diese Aktion kann nicht rückgängig gemacht werden!
          </strong>
          <p>Bist du dir sicher das du folgendes Element löschen möchtest:</p>
          {display ? <p>{display}</p> : <></>}
          <p>
            Typ: {type} / ID: {id}
          </p>
        </>
      ),
    },
  },
  data: {
    none: "Keine",
    not_set: "Nicht gesetzt",
    footprint: {
      label: "Bauform",
      type: "Typ",
    },
    location: {
      label: "Lagerort",
      parent: "Ort",
    },
    product_group: {
      label: "Warengruppe",
      parent: "Obergruppe",
    },
    articleCategory: {
      label: "Artikelkategorie",
      meta: "Felder",
    },
    articleSource: {
      label: "Bezugsquelle",
      label_plural: "Bezugsquellen",
      order_number: "Bestellnummer",
      url: "Web-URL",
      moq: "Mindestbestellmenge",
    },
    distributor: {
      label: "Händler",
      short_name: "Kürzel",
      search_url: "Such-URL",
    },
    inventory_type: {
      label: "Inventar Typ",
      types: {
        journal: "Journal",
        static: "Statisch",
      },
    },
    article: {
      label: "Artikel",
      datasheet_url: "Datenblatt URL",
      fields: "Parameter",
      picture: "Bild",
      matchcode: "Artikel Nummer",
      datasheet: "Datenblatt",
      price_information: {
        label: "Preis Informationen",
        price: {
          label: "Artikelpreis",
          quantity: "Menge",
          create_label: "Preis hinzufügen",
        },
        price_tier: {
          label: "Preisstaffel",
          delete_label: (tier) => `Preisstaffel ${tier} entfernen`,
        },
      },
    },
    keyfield: {
      label: "Schlüsselfeld",
      si: "SI Felder",
      values: "Werte",
      type: "Feldtyp",
      unit: "Einheit",
      required: "Pflichtfeld",
      types: {
        float: "Zahl",
        select: "Auswahl",
        text: "Text",
      },
    },
    id: "ID",
    meta: "Meta",
    name: "Name",
    note: "Notiz",
    display_name: "Anzeige Name",
    description: "Beschreibung",
    moq: "Mindestbestellmenge",
    quantity_scale: {
      label: "Mengenstaffel",
      list: "Mengenstaffeln",
      price: "Preis",
      quantity: "Menge",
    },
    required: "Pflicht",
    strings_select_empty: "Tippe um Elemente hinzuzufügen",
    bool: {
      true: "Ja",
      false: "Nein",
    },
    timestamp: "Zeitpunkt",
    history: {
      label: "Journal",
      types: {
        create: "Erstellt",
        update: "Aktualisiert",
        delete: "Gelöscht",
        add: "Hinzugefügt",
        remove: "Entfernt",
      },
    },
    customer: {
      company: "Firma",
      label: "Kunde",
      name: "Name",
      address: "Adresse",
    },
    fields: {
      label: "Felder",
      forward_voltage: "Vorwärts Spannung",
      forward_current: "Vorwärts Strom",
      power: "Leistung",
      inductance: "Induktivität",
      capacitance: "Kapazität",
      resistance: "Widerstand",
      resistance_typical: "Typischer Widerstand",
      resistance_max: "Maximaler Widerstand",
      current: "Strom",
      current_sat: "Sättigungsstrom",
      wavelength: "Wellenlänge",
      key_fields: "Schlüssel Felder",
      tolerance: "Toleranz",
      voltage: "Spannung",
      voltage_drop: "Spannungs Abfall",
      operating_temperature_min: "Betriebstemperatur min.",
      operating_temperature_max: "Betriebstemperatur max.",
      dielectric: "Dielektrikum",
      temperature_coefficient: "Temperaturkoeffizient",
      length: "Länge",
      cross_section: "Querschnitt",
      pitch: "Rastermaß",
      pin_count: "Pinanzahl",
      max_current: "Max. Strom",
      max_voltage: "Max. Spannung",
    },
    article_categories: {
      capacitor: "Kondensator",
      led: "Led",
      cable: "Kabel",
      resistor: "Widerstand",
      inductor: "Induktivität",
      diode: "Diode",
      "integrated circuit": "IC",
      connector: "Steckverbinder",
    },
    units: {
      volt: "Volt",
      ohm: "Ohm",
      farad: "Farad",
      henry: "Henry",
      ampere: "Ampere",
      meter: "Meter",
    },
    customerOrder: {
      created_at: "Erstellungszeitpunkt",
      customer: "Kunde",
      customerContact: "Kunden Kontakt",
      customerName: "Kunden Name",
      customerReference: "Kunden Referenz",
      delivery_date: "Geplante Fertigstellung",
      express: "Express",
      notes: "Bemerkung",
      positions: "Positionen",
      state: "Status",
      type: "Typ",
      clerk: "Sachbearbeiter",
      updated_at: "Letzte Aktualisierung",
      states: {
        accepted: "Angenommen",
        pickedup: "Abgeholt",
        processing: "In Arbeit",
        ready_for_pickup: "Fertig zur Abholung",
        wait_for_parts: "Warte auf Ware",
        wait_for_reply: "Warte auf Rückmeldung",
        canceled: "Storniert",
      },
      types: {
        inquiry: "Anfrage",
        part_order: "Bestellung",
        repair: "Reparatur",
      },
      order_number: "Bestellnummer",
      text: "Bestellposition - Text",
    },
    user: {
      label: "Benutzer",
      id: "ID",
      displayname: "Anzeige Name",
      email: "Email",
      username: "Benutzername",
      password: "Passwort",
    },
    order_positions: {
      sum: "Gesammtsumme",
    },
    permissions: {
      label: "Berechtigungen",
      "*.*": "root",
    },
  },
};
